import { Component } from '@theme/component';

/**
 * @typedef {object} FloatingVideoRefs
 * @property {HTMLVideoElement} preview - The small muted looping video
 * @property {HTMLDialogElement} dialog - The fullscreen dialog
 * @property {HTMLVideoElement} fullVideo - The fullscreen video with sound and controls
 */

/**
 * A small muted video that floats in a corner of the screen.
 * Clicking it opens the video fullscreen, muted; the unmute button opens it fullscreen with sound.
 * @extends {Component<FloatingVideoRefs>}
 */
class FloatingVideo extends Component {
  requiredRefs = ['preview', 'dialog', 'fullVideo'];

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #resizeObserver = new ResizeObserver(() => this.#syncLayoutVars());
  #ownsLayoutVars = false;

  connectedCallback() {
    // Live directly under <body> so no section's stacking context can cover the video
    if (this.parentElement !== document.body) {
      queueMicrotask(() => this.#moveToBody());
      return;
    }

    super.connectedCallback();

    this.refs.dialog.addEventListener('close', this.#handleDialogClose);
    this.refs.dialog.addEventListener('click', this.#handleDialogClick);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:unload', this.#handleSectionUnload);
    }

    this.hidden = false;
    this.#resizeObserver.observe(this);
    this.#playPreview();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#clearLayoutVars();

    this.refs.dialog?.removeEventListener('close', this.#handleDialogClose);
    this.refs.dialog?.removeEventListener('click', this.#handleDialogClick);
    document.removeEventListener('shopify:section:unload', this.#handleSectionUnload);
  }

  #moveToBody() {
    if (!this.isConnected) return;

    const existing = [...document.querySelectorAll('body > floating-video')].find((element) => element !== this);

    // The section was re-rendered (e.g. a variant change): keep the video that's already playing
    if (existing instanceof HTMLElement && existing.dataset.videoId === this.dataset.videoId) {
      this.remove();
      return;
    }

    existing?.remove();
    document.body.append(this);
  }

  /** @param {Event & { detail?: { sectionId?: string } }} event */
  #handleSectionUnload = (event) => {
    // In the theme editor, remove the moved video when its section reloads or is removed
    if (event.detail?.sectionId === this.dataset.sectionId) this.remove();
  };

  open() {
    this.#openFullscreen({ withSound: false });
  }

  openWithSound() {
    this.#openFullscreen({ withSound: true });
  }

  /** @param {{ withSound: boolean }} options */
  #openFullscreen({ withSound }) {
    const { dialog, fullVideo, preview } = this.refs;

    preview.pause();
    dialog.showModal();
    fullVideo.muted = !withSound;
    fullVideo.currentTime = 0;
    fullVideo.play().catch(() => {});
  }

  close() {
    this.refs.dialog.close();
  }

  /** Hides the video for this page view only; it comes back on refresh or the next page */
  dismiss() {
    this.refs.preview.pause();
    this.hidden = true;
    this.#resizeObserver.disconnect();
    this.#clearLayoutVars();
  }

  #handleDialogClose = () => {
    this.refs.fullVideo.pause();
    this.#playPreview();
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    // A click on the dark area around the video closes it
    if (event.target === this.refs.dialog) this.close();
  };

  #playPreview() {
    const { preview } = this.refs;

    if (this.#reducedMotion.matches) return;

    preview.muted = true;
    preview.play().catch(() => {});
  }

  /** Shares the video's height and right edge on <body> so the WhatsApp button can line up with it */
  #syncLayoutVars() {
    const { style } = document.body;

    style.setProperty('--floating-video-height', `${this.offsetHeight}px`);

    if (this.classList.contains('floating-video--bottom-right')) {
      style.setProperty('--floating-video-right', `${parseFloat(getComputedStyle(this).right) || 0}px`);
    }

    this.#ownsLayoutVars = true;
  }

  #clearLayoutVars() {
    if (!this.#ownsLayoutVars) return;

    document.body.style.removeProperty('--floating-video-height');
    document.body.style.removeProperty('--floating-video-right');
    this.#ownsLayoutVars = false;
  }
}

if (!customElements.get('floating-video')) {
  customElements.define('floating-video', FloatingVideo);
}
